<script lang="ts" setup>
import { computed, onMounted, shallowRef } from 'vue';
import SidebarHeader from '@src/components/views/HomeView/Sidebar/SidebarHeader.vue';
import SearchInput from '@src/components/ui/inputs/SearchInput.vue';
import InternalChatUserItem from './InternalChatUserItem.vue';
import { useInternalChatStore } from '@src/stores/internalChat';
import InformationIcon from "@src/components/ui/utils/InformationIcon.vue";

const store = useInternalChatStore();
const search = shallowRef('');

const sortUsers = (users: any[]) => {
    return [...users].sort((a, b) => {
        const aLastMessage = a.messages?.[0] ?? null;
        const bLastMessage = b.messages?.[0] ?? null;

        const aTime = aLastMessage?.created_at
            ? new Date(aLastMessage.created_at).getTime()
            : 0;

        const bTime = bLastMessage?.created_at
            ? new Date(bLastMessage.created_at).getTime()
            : 0;

        if (aTime && bTime) {
            return bTime - aTime;
        }

        if (aTime && !bTime) {
            return -1;
        }

        if (!aTime && bTime) {
            return 1;
        }

        return a.name.localeCompare(b.name, 'pt-BR', {
            sensitivity: 'base',
        });
    });
};

const filteredUsers = computed(() => {
    const q = search.value.toLowerCase().trim();

    let users = store.users;

    if (q) {
        users = users.filter(u =>
            u.name.toLowerCase().includes(q)
        );
    }

    return sortUsers(users);
});

onMounted(() => {
    if (!store.users.length) store.loadUsers();
});
</script>

<template>
    <div class="flex flex-col h-full">
        <SidebarHeader>
            <template #title>
              Chat Interno
              <InformationIcon title="Chat Interno" text="Converse com sua equipe internamente." class="mb-2"></InformationIcon>
            </template>
        </SidebarHeader>

        <div class="px-5 pb-4">
            <SearchInput
                :value="search"
                @value-changed="(v) => search = v"
            />
        </div>

        <div
            v-if="store.isLoadingUsers"
            class="flex-1 flex items-center justify-center"
        >
            <svg class="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" d="M4 12a8 8 0 018-8v8H4z" fill="currentColor"/>
            </svg>
        </div>

        <div
            v-else
            class="flex-1 overflow-y-auto scrollbar-hidden"
        >
            <InternalChatUserItem
                v-for="user in filteredUsers"
                :key="user.id"
                :is-selected="store.selectedUserId === user.id"
                :unread-count="store.unreadCounts[user.id] ?? 0"
                :user="user"
                @click="store.selectUser(user.id)"
            />

            <div v-if="!filteredUsers.length" class="px-5 py-8 text-center text-sm text-gray-400">
                Nenhum contato encontrado.
            </div>
        </div>
    </div>
</template>
